import { store } from '@/store';
import { ReportLabels } from '@/features/reports/models';
import { Chart, ChartDataset, Plugin, TooltipItem, TooltipModel } from 'chart.js';
import { ChartDataFormat } from '@/features/reports/models/chartDataFormat';

// Define the plugin options type
interface InlineLabelsOptions {
  reportId: string;
  reverseLabels?: boolean;
  dataFormat?: ChartDataFormat;
  defaultLabelValues?: Map<string, string>;
}

// Extend Chart.js types to include 'inlineLabels' plugin options
declare module 'chart.js' {
  interface PluginOptionsByType<TType> {
    inlineLabels?: InlineLabelsOptions;
  }
}

// Export the plugin with Chart.js 4 plugin structure
export const InlineLabelsPlugin: Plugin<'bar' | 'line', InlineLabelsOptions> = {
  id: 'inlineLabels',
  afterInit,
  afterUpdate,
};

function afterInit(chart: Chart) {
  const options = chart.options.plugins?.inlineLabels;
  if (!options) return; // Exit if inlineLabels isn’t configured

  const { reportId, reverseLabels = false, dataFormat, defaultLabelValues } = options;
  const defaults = defaultLabelValues
    ? (defaultLabelValues as Map<string, string>)
    : calculateDefaults(chart, dataFormat);

  setDefaults(reportId!, chart, defaults, reverseLabels);

  // Register event listener to reset labels on mouse leave
  chart.canvas?.addEventListener('mouseleave', () => {
    setDefaults(reportId!, chart, defaults, reverseLabels);
  });

  // Configure tooltip to hide and update labels
  if (!chart.options.plugins) chart.options.plugins = {};
  if (!chart.options.plugins.tooltip) chart.options.plugins.tooltip = {};

  chart.options.plugins.tooltip.enabled = false; // Hide default tooltip
  chart.options.plugins.tooltip.mode = 'index'; // Trigger tooltip for all points on the x-axis
  chart.options.plugins.tooltip.intersect = false; // Trigger even if not directly over a bar
  chart.options.plugins.tooltip.external = (context: { chart: Chart; tooltip: TooltipModel<any> }) => {
    const tooltip = context.tooltip;
    if (tooltip.opacity === 0) return;

    const item = tooltip.dataPoints[0];
    const xAxisIndex = item.dataIndex;
    const xAxisLabel = String(item.label);
    setLabelsForDataPoint(reportId!, xAxisLabel, chart, xAxisIndex, reverseLabels);
  };
}

function afterUpdate(chart: Chart) {
  const options = chart.options.plugins?.inlineLabels;
  if (!options) return; // Exit if inlineLabels isn’t configured

  const { reportId, reverseLabels = false } = options;
  const xAxisIndex = chart.data.labels!.length - 1;
  const xAxisLabel = String(chart.data.labels![xAxisIndex]);
  setLabelsForDataPoint(reportId!, xAxisLabel, chart, xAxisIndex, reverseLabels); // Fixed syntax
}

function calculateDefaults(chart: Chart, dataFormat?: ChartDataFormat): Map<string, string> {
  const defaultLabels = new Map<string, string>();

  chart.data.datasets!.forEach((dataset) => {
    let total = 0;
    dataset.data?.forEach((value) => {
      if (typeof value === 'number') total += value;
    });
    defaultLabels.set(dataset.label!, total.toLocaleString());
  });

  if (dataFormat === ChartDataFormat.PERCENTAGE) {
    let overallTotal = 0;
    defaultLabels.forEach((labelValue) => (overallTotal += parseInt(labelValue.replace(/,/g, ''), 10)));
    defaultLabels.forEach((labelValue, labelName, totals) => {
      const value = labelValue ? parseInt(labelValue.replace(/,/g, ''), 10) : 0;
      const percentageOfTotal = overallTotal === 0 ? 0 : (value / overallTotal) * 100;
      const roundedPercentage = Math.round(percentageOfTotal * 100) / 100;
      totals.set(labelName, `${roundedPercentage}%`);
    });
  }
  return defaultLabels;
}

function setDefaults(reportId: string, chart: Chart, labelData: Map<string, string>, reverseLabels: boolean) {
  const visibleChartData = getVisibleChartData(chart);

  const labels = visibleChartData.map((dataset) => ({
    label: dataset.label,
    value: labelData.get(dataset.label!),
    borderColour: dataset.borderColor as string,
    fillColour: dataset.backgroundColor as string,
  }));

  store.commit('SET_LABELS', {
    reportId,
    scaleTitle: 'Period',
    scaleValue: store.getters.selectedDateOption.label,
    labels,
    reverse: reverseLabels,
  } as ReportLabels);
}

function setLabelsForDataPoint(
  reportId: string,
  scaleValue: string,
  chart: Chart,
  xAxisIndex: number,
  reverseLabels: boolean,
) {
  const visibleData = getVisibleChartData(chart);

  store.commit('SET_LABELS', {
    reportId,
    scaleTitle: getTitle(chart),
    scaleValue,
    labels: visibleData.map((dataset, dataSetIndex) => ({
      label: dataset.label,
      value: getDisplayValue(chart, dataset.data![xAxisIndex], xAxisIndex, dataSetIndex),
      borderColour: dataset.borderColor as string,
      fillColour: dataset.backgroundColor as string,
    })),
    reverse: reverseLabels,
  } as ReportLabels);
}

function getVisibleChartData(chart: Chart): ChartDataset<any>[] {
  const datasets = chart.data.datasets!;
  const legendItems = chart.legend?.legendItems;

  if (!legendItems) return datasets;

  const enabledLegendItems = legendItems.filter((item) => !item.hidden);
  return datasets.filter((dataset) => enabledLegendItems.some((item) => item.text === dataset.label));
}

function getTitle(chart: Chart): string {
  const xScale = chart.options.scales?.x as any;
  return xScale?.title?.text || '-';
}

function getDisplayValue(chart: Chart, value: any, index: number, datasetIndex: number): string {
  const dataset = chart.data.datasets[datasetIndex];
  const tooltipCallback = chart.options.plugins?.tooltip?.callbacks?.label as any;
  if (tooltipCallback) {
    try {
      const formattedValue = tooltipCallback({
        datasetIndex,
        label: 'blah',
        dataIndex: index,
        formattedValue: String(value),
        raw: value,
        dataset,
      });
      return String(formattedValue || value); // Fallback to value if undefined
    } catch (err: any) {
      console.warn('Issue occurred setting inline label', err);
    }
  }
  return value != null ? String(value) : '';
}
